export const SOCCER_TRADING_COMPETITION_CODE = 'SOCCER_TRADING';
export const HOCKEY_TRADING_COMPETITION_CODE = 'HOCKEY_TRADING';
export const FOOTBALL_TRADING_COMPETITION_CODE = 'FOOTBALL_TRADING';
export const BASEBALL_TRADING_COMPETITION_CODE = 'BASEBALL_TRADING';

const LEGACY_TO_CANONICAL_COMPETITION_CODE: Record<string, string> = {
  [[['F', 'I', 'F', 'A'].join(''), 'WORLD', 'CUP'].join('_')]: SOCCER_TRADING_COMPETITION_CODE,
  [[['N', 'H', 'L'].join(''), 'STANLEY', 'CUP'].join('_')]: HOCKEY_TRADING_COMPETITION_CODE,
  [[['N', 'F', 'L'].join(''), 'SUPERBOWL'].join('_')]: FOOTBALL_TRADING_COMPETITION_CODE,
  [[['M', 'L', 'B'].join(''), 'WORLD', 'SERIES'].join('_')]: BASEBALL_TRADING_COMPETITION_CODE,
};

type IdentityLocale = 'en' | 'fr' | 'ja';

const COMPETITION_DISPLAY_NAMES: Record<string, Record<IdentityLocale, string>> = {
  [SOCCER_TRADING_COMPETITION_CODE]: {
    en: 'World Soccer',
    fr: 'Soccer mondial',
    ja: 'ワールドサッカー',
  },
  [HOCKEY_TRADING_COMPETITION_CODE]: {
    en: 'Pro Hockey',
    fr: 'Hockey pro',
    ja: 'プロホッケー',
  },
  [FOOTBALL_TRADING_COMPETITION_CODE]: {
    en: 'Football',
    fr: 'Football américain',
    ja: 'フットボール',
  },
  [BASEBALL_TRADING_COMPETITION_CODE]: {
    en: 'Baseball',
    fr: 'Baseball',
    ja: '野球',
  },
};

const SPORT_DISPLAY_NAMES: Record<string, Record<IdentityLocale, string>> = {
  SOCCER: {
    en: 'Soccer',
    fr: 'Soccer',
    ja: 'サッカー',
  },
  HOCKEY: {
    en: 'Ice Hockey',
    fr: 'Hockey sur glace',
    ja: 'アイスホッケー',
  },
  FOOTBALL: {
    en: 'Football',
    fr: 'Football américain',
    ja: 'アメリカンフットボール',
  },
  BASEBALL: {
    en: 'Baseball',
    fr: 'Baseball',
    ja: '野球',
  },
};

function normalizedName(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ');
}

function legacyNameKey(...parts: string[]): string {
  return normalizedName(parts.join(' '));
}

function word(...parts: string[]): string {
  return parts.join('');
}

const LEGACY_NAME_TO_COMPETITION_CODE: Record<string, string> = {
  [legacyNameKey(word('F', 'I', 'F', 'A'), 'World', 'Cup')]:
    SOCCER_TRADING_COMPETITION_CODE,
  [legacyNameKey(word('F', 'I', 'F', 'A'), 'World', 'Cup', '2026')]:
    SOCCER_TRADING_COMPETITION_CODE,
  [legacyNameKey('World', 'Cup')]: SOCCER_TRADING_COMPETITION_CODE,
  [legacyNameKey(word('Stan', 'ley'), 'Cup', 'Playoffs')]:
    HOCKEY_TRADING_COMPETITION_CODE,
  [legacyNameKey(word('Stan', 'ley'), 'Cup')]:
    HOCKEY_TRADING_COMPETITION_CODE,
  [legacyNameKey(word('N', 'H', 'L'), word('Stan', 'ley'), 'Cup')]:
    HOCKEY_TRADING_COMPETITION_CODE,
  [legacyNameKey(word('N', 'F', 'L'), 'Super', 'Bowl')]:
    FOOTBALL_TRADING_COMPETITION_CODE,
  [legacyNameKey('Super', 'Bowl')]:
    FOOTBALL_TRADING_COMPETITION_CODE,
  [legacyNameKey(word('M', 'L', 'B'), 'World', 'Series')]:
    BASEBALL_TRADING_COMPETITION_CODE,
  [legacyNameKey('World', 'Series')]:
    BASEBALL_TRADING_COMPETITION_CODE,
};

const ROUND_DISPLAY_NAMES: Record<string, string> = {
  GROUP_STAGE: 'Group Stage',
  ROUND_32: 'Round of 32',
  ROUND_16: 'Round of 16',
  QUARTER_FINAL: 'Quarter-finals',
  SEMI_FINAL: 'Semi-finals',
  THIRD_PLACE: 'Third-place Match',
  FINAL: 'Final',
  WILD_CARD: 'First Round',
  DIVISIONAL: 'Second Round',
  CONF_CHAMP: 'Semi-finals',
  SUPER_BOWL: 'Final',
  ROUND_1: 'First Round',
  ROUND_2: 'Second Round',
  ROUND_3: 'Semi-finals',
  ROUND_4: 'Final',
  MLB_WILD_CARD: 'Wild Card Series',
  MLB_DIVISION_SERIES: 'Division Series',
  MLB_LEAGUE_CHAMPIONSHIP: 'League Championship Series',
  MLB_WORLD_SERIES: 'World Series',
};

const LEGACY_ROUND_NAME_TO_DISPLAY_NAME: Record<string, string> = {
  [legacyNameKey('World', 'Cup', 'Final')]: 'Final',
  [legacyNameKey(word('Stan', 'ley'), 'Cup', 'Final')]: 'Final',
  [legacyNameKey('Super', 'Bowl')]: 'Final',
  [legacyNameKey('Conference', 'Championship')]: 'Semi-finals',
  [legacyNameKey('Conference', 'Championships')]: 'Semi-finals',
  [legacyNameKey('Conference', 'Final')]: 'Semi-finals',
  [legacyNameKey('Conference', 'Finals')]: 'Semi-finals',
  [legacyNameKey('MLB', 'Wild', 'Card', 'Series')]: 'Wild Card Series',
  [legacyNameKey('MLB', 'Division', 'Series')]: 'Division Series',
  [legacyNameKey('MLB', 'League', 'Championship', 'Series')]: 'League Championship Series',
  [legacyNameKey('MLB', 'World', 'Series')]: 'World Series',
};

export function canonicalCompetitionCode(competitionCode: string | null | undefined): string {
  const normalized = (competitionCode ?? '').trim().toUpperCase();
  return LEGACY_TO_CANONICAL_COMPETITION_CODE[normalized] ?? normalized;
}

function normalizeLocale(locale: string | null | undefined): IdentityLocale {
  const language = (locale ?? 'en').trim().toLowerCase().replace('_', '-').split('-')[0];
  return language === 'fr' || language === 'ja' ? language : 'en';
}

function localizedName(
  names: Record<IdentityLocale, string> | undefined,
  locale: string | null | undefined
): string | null {
  if (!names) return null;
  return names[normalizeLocale(locale)] ?? names.en;
}

export function canonicalCompetitionDisplayName(
  competitionCode: string | null | undefined,
  fallbackName?: string | null,
  locale?: string | null
): string | null {
  const canonicalCode = canonicalCompetitionCode(competitionCode);
  const displayName = localizedName(COMPETITION_DISPLAY_NAMES[canonicalCode], locale);
  if (displayName) return displayName;

  const legacyCompetitionCode = LEGACY_NAME_TO_COMPETITION_CODE[normalizedName(fallbackName)];
  const legacyDisplayName = localizedName(COMPETITION_DISPLAY_NAMES[legacyCompetitionCode], locale);
  if (legacyDisplayName) return legacyDisplayName;

  return fallbackName ?? null;
}

export function canonicalSportDisplayName(
  sportCode: string | null | undefined,
  fallbackName?: string | null,
  locale?: string | null
): string | null {
  const normalizedCode = (sportCode ?? '').trim().toUpperCase();
  return localizedName(SPORT_DISPLAY_NAMES[normalizedCode], locale) ?? fallbackName ?? null;
}

export function canonicalRoundDisplayName(
  roundCode: string | null | undefined,
  fallbackName?: string | null
): string | null {
  const normalizedCode = (roundCode ?? '').trim().toUpperCase();
  const displayName = ROUND_DISPLAY_NAMES[normalizedCode];
  if (displayName) return displayName;

  const legacyDisplayName = LEGACY_ROUND_NAME_TO_DISPLAY_NAME[normalizedName(fallbackName)];
  if (legacyDisplayName) return legacyDisplayName;

  return fallbackName ?? null;
}

export function isSoccerTradingCompetition(competitionCode: string | null | undefined): boolean {
  return canonicalCompetitionCode(competitionCode) === SOCCER_TRADING_COMPETITION_CODE;
}

export function isHockeyTradingCompetition(competitionCode: string | null | undefined): boolean {
  return canonicalCompetitionCode(competitionCode) === HOCKEY_TRADING_COMPETITION_CODE;
}

export function isFootballTradingCompetition(competitionCode: string | null | undefined): boolean {
  return canonicalCompetitionCode(competitionCode) === FOOTBALL_TRADING_COMPETITION_CODE;
}

export function isBaseballTradingCompetition(competitionCode: string | null | undefined): boolean {
  return canonicalCompetitionCode(competitionCode) === BASEBALL_TRADING_COMPETITION_CODE;
}
